import {
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
  signal
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Order } from '@infrab4a/connect';
import { TableColumn } from 'src/app/components/table';
import { ShopOrderService } from 'src/app/connect-api/api/shop/shop-order.service';
import { ShopMap } from 'src/app/connect-api/enums/ShopMap';
import {
  PaymentMethod,
  getAllPaymentMethod
} from 'src/app/connect-api/models/PaymentMethod';
import {
  PaymentStatus,
  getAllPaymentStatus
} from 'src/app/connect-api/models/PaymentStatus';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';

@Component({
  selector: 'app-orders-resend',
  templateUrl: './orders-resend.component.html',
  styleUrl: './orders-resend.component.scss'
})
export class OrdersResendComponent implements OnInit {
  protected shopOrdersService = inject(ShopOrderService);
  protected title = inject(Title);
  private cdRef = inject(ChangeDetectorRef);

  protected orders: Array<Order>;
  protected originalCols = [
    new TableColumn.Builder()
      .setHeader('Id')
      .setField('orderNumber')
      .setRouterLink('/shop-orders/order/')
      .setRouterLinkFieldName('id')
      .build(),
    new TableColumn.Builder()
      .setHeader('Comprador')
      .setField('user.displayName')
      .setCondition('contains')
      .setDisplayFunction((order: Order) => `${order.user.displayName}`)
      .build(),
    new TableColumn.Builder()
      .setHeader('CPF')
      .setField('user.cpf')
      .setCondition('contains')
      .setDisplayFunction((order: Order) =>
        order?.user?.cpf ? FormUtil.cpfFormatted(Number(order.user.cpf)) : null
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Email')
      .setField('user.email')
      .setCondition('contains')
      .setDisplayFunction((order: Order) => order.user.email)
      .build(),
    new TableColumn.Builder()
      .setHeader('Forma Pagamento')
      .setField('payment.paymentMethod')
      .setCondition('in')
      .setDisplayFunction(
        (order: Order) =>
          PaymentMethod[
            order.payment.payment_method || order.payment['paymentMethod']
          ]
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('TID')
      .setField('payment.id')
      .setType('number')
      .setDisplayFunction((order: Order) => order.payment.id)
      .build(),
    new TableColumn.Builder()
      .setHeader('Status Pagamento')
      .setField('payment.status')
      .setCondition('in')
      .setDisplayFunction((order: Order) => PaymentStatus[order.payment.status])
      .build(),
    new TableColumn.Builder()
      .setHeader('Produtos')
      .setField('lineItems.length')
      .setType('formattedInteger')
      .setCondition('gte')
      .build(),
    new TableColumn.Builder()
      .setHeader('Loja')
      .setField('shop')
      .setCondition('in')
      .setDisplayFunction((order: Order) => ShopMap[order.shop])
      .build(),
    new TableColumn.Builder()
      .setHeader('Transportadora')
      .setField('shipping.ShippingCompanyName')
      .setCondition('contains')
      .setDisplayFunction((order: Order) => order.shipping?.ShippingCompanyName)
      .build(),
    new TableColumn.Builder()
      .setHeader('Status')
      .setField('status')
      .setCondition('in')
      .build(),
    new TableColumn.Builder()
      .setHeader('Cupom')
      .setField('coupon.nickname')
      .setDisplayFunction((order: Order) => order.coupon?.nickname)
      .build(),
    new TableColumn.Builder()
      .setHeader('Data')
      .setField('createdAt')
      .setType('date')
      .setCondition('gte')
      .setFilter(false)
      .build(),
    new TableColumn.Builder()
      .setHeader('Total')
      .setField('totalPrice')
      .setType('currency')
      .setCondition('gte')
      .build(),
    new TableColumn.Builder()
      .setHeader('Prazo')
      .setField('shipping.DaysToDelivery')
      .setDisplayFunction((order: Partial<Order>) =>
        order.shipping?.DaysToDelivery
          ? `${order.shipping.DaysToDelivery} dia(s)`
          : (order.shipping as any)?.description || 'Indeterminado'
      )
      .setCondition('gte')
      .build(),
    new TableColumn.Builder()
      .setHeader('Data entrega')
      .setField('shipping.additionalDescription')
      .setDisplayFunction((order: Partial<Order>) =>
        (order.shipping as any)?.additionalDescription
          ? (order.shipping as any)?.additionalDescription
          : this.shopOrdersService?.deliveryDate(order) || 'Indeterminado'
      )
      .setCondition('contains')
      .build()
  ];
  visibleColumns: TableColumn[];

  dropdownFilters = {
    status: [
      { label: 'Aguardando pagamento', value: 'Aguardando pagamento' },
      { label: 'Preparando pedido', value: 'Preparando pedido' },
      { label: 'Enviado', value: 'Enviado' },
      { label: 'Entregue', value: 'Entregue' },
      { label: 'Cancelado', value: 'Cancelado' }
    ],
    shop: [
      { label: ShopMap.Glamshop, value: 'Glamshop' },
      { label: ShopMap.Glampoints, value: 'Glampoints' },
      { label: ShopMap.mensmarket, value: 'mensmarket' }
    ],
    'payment.paymentMethod': getAllPaymentMethod(),
    'payment.status': getAllPaymentStatus()
  };

  referenceForm = signal(
    new FormGroup({
      columns: new FormControl<Array<TableColumn>>([], Validators.minLength(1))
    })
  );
  totalCols = 0;

  async ngOnInit(): Promise<void> {
    this.title.setTitle('Pedidos junto com Edição');
    this.visibleColumns = [...this.originalCols];
    this.referenceForm.update((form) => {
      form.controls['columns'].setValue(this.originalCols);
      return form;
    });
    await this.findOrders();
  }

  async findOrders(): Promise<void> {
    try {
      LoaderService.showLoader();
      const now = new Date();

      const date60DaysAgo = FormUtil.utcDate(new Date());
      date60DaysAgo.setDate(now.getDate() - 60);
      date60DaysAgo.setHours(-3);
      date60DaysAgo.setMinutes(0);
      date60DaysAgo.setSeconds(0);
      date60DaysAgo.setMilliseconds(0);

      const date35DaysAgo = new Date();
      date35DaysAgo.setDate(now.getDate() - 35);
      date35DaysAgo.setHours(23);
      date35DaysAgo.setMinutes(59);
      date35DaysAgo.setSeconds(59);
      date35DaysAgo.setMilliseconds(999);

      const result = await this.shopOrdersService.getOrdersList({
        filters: [
          {
            condition: 'gte',
            field: 'createdAt',
            fieldType: 'date',
            value: date60DaysAgo
          },
          {
            condition: 'lte',
            field: 'createdAt',
            fieldType: 'date',
            value: date35DaysAgo
          },
          {
            condition: 'equals',
            field: 'status',
            fieldType: 'text',
            value: 'Preparando pedido'
          },
          {
            condition: 'equals',
            field: 'isEditionOrder',
            fieldType: 'boolean',
            value: true
          }
        ],
        sortBy: 'createdAt',
        sortDirection: 'asc',
        page: 0,
        pageSize: 9999
      });

      this.orders = result.content
        .map((o) => {
          const firstName = o.user?.firstName;
          const lastName = o.user?.lastName;
          const nameParts = [];
          if (firstName) {
            nameParts.push(firstName);
          }
          if (lastName) {
            nameParts.push(lastName);
          }
          const builtName = nameParts.join(' ');
          if (builtName) {
            o.user.displayName = builtName;
          }
          return o;
        });
    } catch (error) {
      this.orders = [];
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async reloadTable(refresh = true): Promise<void> {
    if (refresh) {
      await this.findOrders();
    } else if (
      this.referenceForm().value.columns?.length &&
      (this.referenceForm().value.columns.length !==
        this.visibleColumns.length ||
        this.referenceForm().value.columns.some((c) =>
          this.visibleColumns.every((cd) => cd.field !== c.field)
        ))
    ) {
      LoaderService.showLoader();
      const orders = [...this.orders];
      delete this.orders;
      setTimeout(() => {
        this.visibleColumns = this.originalCols.filter((c) =>
          this.referenceForm().value.columns.some((rc) => rc.field === c.field)
        );
        this.orders = [...orders];
        LoaderService.showLoader(false);
      }, 1000);
    }
  }

  updateTotalCols() {
    this.totalCols = this.visibleColumns?.length || 0;
    this.cdRef.detectChanges();
  }

  get columnsLabel(): string {
    return this.referenceForm().value.columns.length ===
      this.originalCols?.length
      ? 'Todas'
      : `${this.referenceForm().value.columns.length} colunas`;
  }
}
